import React, { useState, useEffect } from 'react';
import StableMuiDialog from '../../../../common/StableMuiDialog.js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import {
  colors,
  DIALOG_PAPER_SX,
  CANCEL_BUTTON_SX,
  PRIMARY_BUTTON_SX,
  SectionLabel,
  OptionsPanel,
} from '../../Offers/TownPortalFlow/TownPortalOffersDialog.js';
import axiosInstance from '../../../../fetchConfig/axiosConfig.js';
import { usePersistedDataStore } from '../../../store.js';
import { useUser } from '../../../UserContext.js';
import { toast } from '../../../../common/toast';
import logger from '../../../../utils/logger';

const AdjournCasesDialog = ({ open, onClose, rows }) => {
  const { user } = useUser();
  const [adjournmentDate, setAdjournmentDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const caseLabel = `case${rows.length === 1 ? '' : 's'}`;

  useEffect(() => {
    if (open) setAdjournmentDate('');
  }, [open]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axiosInstance.post('/adjourn_cases', {
        cases: rows.map((row) => ({ index_number: row.SCARIndexNumber, parcel_id: row.parcel_id, adjournment_date: adjournmentDate || null })),
        edit_user: Number(user?.userId),
      });
      logger.log('Adjourn cases result', data);
      // Index numbers can span parcels, so only patch the selected parcels whose index was updated.
      const updatedIndexes = new Set(data.cases || []);
      const updatedParcelIds = new Set(rows.filter((row) => updatedIndexes.has(row.SCARIndexNumber)).map((row) => row.parcel_id));
      const { negotiationObj, negotiationObjVersion, setNegotiationObj, setNegotiationObjVersion } = usePersistedDataStore.getState();
      setNegotiationObj({
        ...negotiationObj,
        cases: negotiationObj.cases.map((caseItem) => (updatedParcelIds.has(caseItem.parcel_id)
          ? { ...caseItem, SCARDeterminationAction: 'OA', ...(adjournmentDate && { SCARHearingDate: adjournmentDate }) }
          : caseItem)),
      });
      setNegotiationObjVersion(negotiationObjVersion + 1);
      if (data.skipped?.length) {
        toast.warning(`${data.message} ${data.skipped.length} skipped (${[...new Set(data.skipped.map((item) => item.reason))].join(', ')})`);
      } else {
        toast.success(data.message);
      }
      onClose();
    } catch (error) {
      logger.error('Adjourn cases failed', error);
      toast.error(error?.response?.data?.error || error.message || `Failed to adjourn ${caseLabel}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StableMuiDialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth={false}
      PaperProps={{ sx: { ...DIALOG_PAPER_SX, maxWidth: 480 } }}
    >
      <Box sx={{ p: 3 }}>
        <Typography component="h2" sx={{ fontSize: 17, fontWeight: 500, color: colors.textPrimary, m: '0 0 4px' }}>
          {`Open adjourn ${caseLabel}`}
        </Typography>
        <Typography sx={{ fontSize: 13, color: colors.textSecondary, m: '0 0 20px' }}>
          {`Mark ${rows.length} selected ${caseLabel} as open adjourned (OA)`}
        </Typography>

        <SectionLabel>Adjournment</SectionLabel>
        <OptionsPanel visible>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary, m: '0 0 2px' }}>
                Adjournment date (optional)
              </Typography>
              <Typography sx={{ fontSize: 11, color: colors.textSecondary, m: 0, lineHeight: 1.4 }}>
                Leave blank to adjourn without a new court date
              </Typography>
            </Box>
            <TextField
              type="date"
              size="small"
              value={adjournmentDate}
              onChange={(e) => setAdjournmentDate(e.target.value)}
              sx={{ width: 160, '& .MuiInputBase-root': { fontSize: 12, borderRadius: '6px' }, '& .MuiInputBase-input': { py: 0.75 } }}
            />
          </Box>
        </OptionsPanel>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 0.5 }}>
          <Button onClick={onClose} disabled={submitting} sx={CANCEL_BUTTON_SX}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disableElevation
            disabled={!rows.length || submitting}
            startIcon={submitting && <CircularProgress size={14} color="inherit" />}
            sx={PRIMARY_BUTTON_SX}
          >
            {submitting ? 'Adjourning...' : `Adjourn ${rows.length} ${caseLabel}`}
          </Button>
        </Box>
      </Box>
    </StableMuiDialog>
  );
};

export default AdjournCasesDialog;
